<template>
  <div class="flex items-center">
    <!-- Delete -->
    <el-tooltip
      v-if="!buttons.delete.hide"
      effect="dark"
      placement="top-start"
      :content="deleteTooltip"
      :disabled="!deleteDisabled || !deleteTooltip"
    >
      <!-- In order for the hover event to propagate when the button is disabled, the button element need to be wrapped with a container element, per element-ui docs -->
      <div>
        <el-button
          v-permissions="{ scopes: buttons.delete.scopes }"
          type="danger"
          plain
          data-testid="delete-button"
          :class="{ disabled: deleteDisabled }"
          :disabled="deleteDisabled"
          @click="$emit('delete')"
          v-text="$t('common.interactions.buttons.delete')"
        />
      </div>
    </el-tooltip>

    <span
      v-if="!buttons.delete.hide && deleteHint"
      class="ml-3 text-center text-sm leading-[22px] text-th-text-regular"
      data-testid="delete-hint"
    >
      {{ deleteHint }}
    </span>

    <div class="grow" />

    <!-- Cancel/Back -->
    <el-button
      v-if="!buttons.cancel.hide"
      data-testid="cancel-button"
      @click="handleCancel"
      v-text="$t('common.interactions.buttons.back')"
    />

    <!-- Close -->
    <el-button
      v-if="!buttons.close.hide"
      data-testid="close-button"
      @click="$emit('close')"
      v-text="$t('common.interactions.buttons.close')"
    />

    <!-- Save -->
    <el-button
      v-if="!buttons.save.hide"
      v-permissions="{ scopes: buttons.save.scopes }"
      type="accent"
      data-testid="save-button"
      :class="{ disabled: saveDisabled }"
      :disabled="saveDisabled"
      @click="$emit('save')"
      v-text="
        buttons.save.type === 'update'
          ? $t('common.interactions.buttons.save')
          : $t('common.interactions.buttons.create')
      "
    />
  </div>
</template>

<script>
import { useRouter } from 'vue-router'

export default {
  props: {
    //`isNew` will toggle update or create save button
    isNew: {
      type: Boolean,
      default: false
    },
    //Buttons permissions prefix.
    permissionPrefix: {
      type: String,
      default: ''
    },
    //Buttons to hide
    excludeButtons: {
      type: Array,
      default: () => []
    },
    //Buttons to show
    includeButtons: {
      type: Array,
      default: () => []
    },
    //Add tooltip to delete button
    deleteTooltip: {
      type: String,
      default: ''
    },
    //Text shown next to the delete button when it is visible
    deleteHint: {
      type: String,
      default: ''
    },
    //Make delete button disabled
    deleteDisabled: {
      type: Boolean,
      default: false
    },
    saveDisabled: {
      type: Boolean,
      default: false
    },
    //Route path for cancel/back action
    returnedPath: {
      type: [String, Object],
      default: null
    }
  },
  emits: ['save', 'delete', 'close'],
  setup(props) {
    const router = useRouter()
    const goBack = () => {
      // return to returnedPath, if set
      if (props.returnedPath) return router.push(props.returnedPath)

      // If no returnedPath is set and browser history has more than one entry, go back in history
      if (window.history.length > 1) return router.back()

      // If history length is 1 or less (e.g. freshly new tab), go to home
      return router.push('/')
    }

    return { goBack }
  },
  computed: {
    buttons() {
      return {
        delete: {
          hide: this.isNew || this.excludeButtons.includes('delete'),
          scopes: this.deletePermissions
        },
        cancel: {
          hide: this.excludeButtons.some((value) =>
            ['cancel', 'back'].includes(value)
          )
        },
        save: {
          hide: this.excludeButtons.includes('save'),
          type: this.isNew ? 'create' : 'update',
          scopes: this.savePermissions
        },
        close: {
          hide: !this.includeButtons.includes('close')
        }
      }
    },
    deletePermissions() {
      return this.permissionPrefix ? [`${this.permissionPrefix}:delete`] : []
    },
    savePermissions() {
      return this.permissionPrefix
        ? [`${this.permissionPrefix}${this.isNew ? ':create' : ':update'}`]
        : []
    }
  },
  methods: {
    handleCancel() {
      this.goBack()
    }
  }
}
</script>

<style scoped></style>
